// components/CardNotificacao.tsx
import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  CardMedia,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  //  Grid, // REMOVIDO: Eliminando o componente Grid
  Chip, // Adicionado para exibir as tags
  Divider, // Adicionado para separar seções
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { styled } from "@mui/material/styles";
import { API_URLS } from "../../config/apiUrls";

// A API passou a armazenar imageUrl como URL absoluta do S3. Registros antigos
// podem guardar um caminho relativo (ex.: "/uploads/..."). Só prefixamos a
// baseUrl quando a URL for relativa, evitando concatenar dois domínios.
const resolveImageUrl = (imageUrl: string): string =>
  /^https?:\/\//i.test(imageUrl)
    ? imageUrl
    : `${API_URLS.baseUrl}${imageUrl}`;

// Interface para as propriedades completas da Notificação (inalterada)
interface Notificacao {
  id: string;
  title: string;
  content: string;
  startDate: string;
  endDate: string;
  maxViews: number;
  imageUrl: string | null;
  isForAllUsers?: boolean;
  listCallsCount?: number;
  specificCallsCount?: number;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  targetUsers?: {
    id: string;
    name: string;
    email: string;
  }[];
}

interface CardNotificacaoProps {
  notification: Notificacao;
  token: string;
  onNotificationActionSuccess: () => void;
}

// Estilização para o Box que conterá os Chips (inalterada)
const ChipsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-start",
  flexWrap: "wrap",
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(1),
  maxHeight: "100px",
  overflowY: "auto",
  paddingRight: theme.spacing(1),
}));

const CardNotificacao: React.FC<CardNotificacaoProps> = ({
  notification,
  token,
  onNotificationActionSuccess,
}) => {
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "warning" | "info"
  >("info");

  // Estados para o formulário de edição
  const [editTitle, setEditTitle] = useState(notification.title);
  const [editContent, setEditContent] = useState(notification.content);
  const [editStartDate, setEditStartDate] = useState(
    new Date(notification.startDate).toISOString().substring(0, 16)
  );
  const [editEndDate, setEditEndDate] = useState(
    new Date(notification.endDate).toISOString().substring(0, 16)
  );
  const [editMaxViews, setEditMaxViews] = useState<number | string>(
    notification.maxViews
  );

  // --- Funções Auxiliares de Formatação (inalteradas) ---
  const formatDateTime = (isoString: string) => {
    if (!isoString) return "N/A";
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "Data Inválida";
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // --- Funções para Confirmação de Exclusão (inalteradas) ---
  const handleClickOpenDeleteConfirm = () => {
    setOpenDeleteConfirm(true);
  };

  const handleCloseDeleteConfirm = () => {
    setOpenDeleteConfirm(false);
  };

  // --- Funções para Edição (inalteradas) ---
  const handleClickOpenEditModal = () => {
    setEditTitle(notification.title);
    setEditContent(notification.content);
    setEditStartDate(
      new Date(notification.startDate).toISOString().substring(0, 16)
    );
    setEditEndDate(
      new Date(notification.endDate).toISOString().substring(0, 16)
    );
    setEditMaxViews(notification.maxViews);
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
  };

  // --- Funções para o Popup da Imagem (inalteradas) ---
  const handleOpenImageModal = () => {
    setOpenImageModal(true);
  };

  const handleCloseImageModal = () => {
    setOpenImageModal(false);
  };

  // --- Funções de Requisição (inalteradas) ---
  const handleDeleteNotification = async () => {
    setLoadingAction(true);
    try {
      const response = await fetch(
        `${API_URLS.notifications}/${notification.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Erro ao excluir notificação.");
      }

      setSnackbarMessage("Notificação excluída com sucesso!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      onNotificationActionSuccess();
      handleCloseDeleteConfirm();
    } catch (err: any) {
      setSnackbarMessage(err.message || "Erro ao excluir notificação.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      console.error("Erro ao excluir notificação:", err);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleEditNotification = async () => {
    setLoadingAction(true);

    const finalMaxViews = Number(editMaxViews);

    if (
      !editTitle ||
      !editContent ||
      !editStartDate ||
      !editEndDate ||
      finalMaxViews <= 0
    ) {
      setSnackbarMessage(
        "Por favor, preencha todos os campos obrigatórios para edição e garanta que 'Máx. Views' seja maior que zero."
      );
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      setLoadingAction(false);
      return;
    }

    try {
      const startDateTime = new Date(editStartDate);
      const endDateTime = new Date(editEndDate);
      const currentDateTime = new Date();

      if (startDateTime >= endDateTime) {
        setSnackbarMessage(
          "A 'Data de Início' deve ser anterior à 'Data de Fim'."
        );
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
        setLoadingAction(false);
        return;
      }

      if (endDateTime <= currentDateTime) {
        setSnackbarMessage(
          "A 'Data de Fim' deve ser posterior à data e hora atuais."
        );
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
        setLoadingAction(false);
        return;
      }

      const updatedData = {
        title: editTitle,
        content: editContent,
        startDate: startDateTime.toISOString(),
        endDate: endDateTime.toISOString(),
        maxViews: finalMaxViews,
      };

      const response = await fetch(
        `${API_URLS.notifications}/${notification.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updatedData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Erro ao editar notificação.");
      }

      setSnackbarMessage("Notificação editada com sucesso!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      onNotificationActionSuccess();
      handleCloseEditModal();
    } catch (err: any) {
      setSnackbarMessage(err.message || "Erro ao editar notificação.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      console.error("Erro ao editar notificação:", err);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <Card
      sx={{
        maxWidth: 345,
        m: 2,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        boxShadow: 3,
      }}
    >
      <Box>
        {notification.imageUrl && (
          <CardMedia
            component="img"
            height="140"
            image={resolveImageUrl(notification.imageUrl)}
            alt={notification.title}
            onClick={handleOpenImageModal}
            sx={{ cursor: "pointer", objectFit: "cover" }}
          />
        )}
        <CardContent>
          <Typography gutterBottom variant="h5" component="div">
            {notification.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {notification.content}
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.primary" component="div">
              Início: {formatDateTime(notification.startDate)}
            </Typography>
            <Typography variant="caption" color="text.primary" component="div">
              Fim: {formatDateTime(notification.endDate)}
            </Typography>
            <Typography variant="caption" color="text.primary" component="div">
              Máx. Views p/ Usuário: {notification.maxViews}
            </Typography>
            <Typography variant="caption" color="text.primary" component="div">
              Tipo:{" "}
              {notification.isForAllUsers
                ? "Global (Todos os Usuários)"
                : "Específica (Usuários Selecionados)"}
            </Typography>
            <Typography variant="caption" color="text.primary" component="div">
              Ativa: {notification.isActive ? "Sim" : "Não"}
            </Typography>
          </Box>
          {/* --- Renderização dos Usuários Alvo (Chips) --- */}
          {!notification.isForAllUsers &&
            notification.targetUsers &&
            notification.targetUsers.length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Usuários Alvo:
                </Typography>
                <ChipsContainer>
                  {notification.targetUsers.map((user) => (
                    <Chip
                      key={user.id}
                      label={`${user.name}`}
                      variant="outlined"
                      color="primary"
                      size="small"
                      sx={{
                        maxWidth: "100%",
                        "& .MuiChip-label": {
                          whiteSpace: "normal",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                      }}
                    />
                  ))}
                </ChipsContainer>
              </>
            )}
          {!notification.isForAllUsers &&
            (!notification.targetUsers ||
              notification.targetUsers.length === 0) && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1, fontStyle: "italic" }}
                >
                  Nenhum usuário alvo definido para esta notificação específica.
                </Typography>
              </>
            )}
          {/* --- Fim da Renderização dos Usuários Alvo --- */}
        </CardContent>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2, gap: 1 }}>
        <Button
          variant="outlined"
          color="info"
          startIcon={<EditIcon />}
          onClick={handleClickOpenEditModal}
        >
          Editar
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleClickOpenDeleteConfirm}
        >
          Excluir
        </Button>
      </Box>

      {/* Dialog de Confirmação de Exclusão (inalterado) */}
      <Dialog
        open={openDeleteConfirm}
        onClose={handleCloseDeleteConfirm}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirmar Exclusão?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Você tem certeza que deseja excluir a notificação "
            <b>{notification.title}</b>
            "? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirm} disabled={loadingAction}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteNotification}
            color="error"
            variant="contained"
            autoFocus
            disabled={loadingAction}
          >
            {loadingAction ? <CircularProgress size={24} /> : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Edição da Notificação */}
      <Dialog
        open={openEditModal}
        onClose={handleCloseEditModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Editar Notificação</DialogTitle>
        <DialogContent>
          <TextField
            label="Título da Notificação"
            fullWidth
            margin="normal"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
          />
          <TextField
            label="Conteúdo da Notificação"
            fullWidth
            margin="normal"
            multiline
            rows={3}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            InputProps={{
              sx: {
                "& .MuiInputBase-inputMultiline": {
                  resize: "vertical",
                },
              },
            }}
          />
          {/* SUBSTITUINDO GRID CONTAINER E GRID ITEM POR BOX FLEXBOX */}
          <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
            {/* Campo Data de Início - Ocupa 50% em sm e 100% em xs */}
            <Box sx={{ width: { xs: "100%", sm: "50%" } }}>
              <TextField
                label="Data de Início"
                type="datetime-local"
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                value={editStartDate}
                onChange={(e) => setEditStartDate(e.target.value)}
              />
            </Box>
            {/* Campo Data de Fim - Ocupa 50% em sm e 100% em xs */}
            <Box sx={{ width: { xs: "100%", sm: "50%" } }}>
              <TextField
                label="Data de Fim"
                type="datetime-local"
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                value={editEndDate}
                onChange={(e) => setEditEndDate(e.target.value)}
              />
            </Box>
          </Box>
          <TextField
            label="Número Máximo de Visualizações"
            type="number"
            fullWidth
            margin="normal"
            value={editMaxViews}
            onChange={(e) => {
              const value = e.target.value;
              setEditMaxViews(value === "" ? "" : Number(value));
            }}
            inputProps={{ min: 0 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal} disabled={loadingAction}>
            Cancelar
          </Button>
          <Button
            onClick={handleEditNotification}
            color="primary"
            variant="contained"
            disabled={loadingAction}
          >
            {loadingAction ? (
              <CircularProgress size={24} />
            ) : (
              "Salvar Alterações"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para exibir a imagem em popup (inalterado) */}
      {notification.imageUrl && (
        <Dialog
          open={openImageModal}
          onClose={handleCloseImageModal}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Visualizar Imagem</DialogTitle>
          <DialogContent
            dividers
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: 0,
            }}
          >
            <img
              src={resolveImageUrl(notification.imageUrl)}
              alt={notification.title}
              style={{ maxWidth: "100%", height: "auto", display: "block" }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseImageModal} color="primary">
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Snackbar para feedback de sucesso/erro (inalterado) */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default CardNotificacao;
